import axios from "axios";
import toast from "react-hot-toast";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/elections`;

// Always send cookies
axios.defaults.withCredentials = true;

/**
 * Internal helper to attach auth header if token is provided
 */
function authConfig(token) {
    return token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
}

/**
 * Fetch all elections
 * @param {string} [token] — Clerk JWT
 * @returns {Promise<Array>}
 */
export async function getAllElections(token) {
    try {
        const response = await axios.get(BASE_URL, authConfig(token));
        return response.data;
    } catch (err) {
        console.error("Error fetching elections:", err);
        toast.error("Failed to fetch elections");
        throw err;
    }
}

/**
 * Register a new election
 * @param {Object} data
 * @param {string} token
 */
export async function registerElection(data, token) {
    try {
        const response = await axios.post(
            `${BASE_URL}/register`,
            new URLSearchParams(data),
            authConfig(token)
        );
        toast.success("Election registered successfully");
        return response.data;
    } catch (err) {
        console.error("Error registering election:", err);
        toast.error(err.response?.data || "Election registration failed");
        throw err;
    }
}

export async function updateElection(id, data, token) {
    try {
        const response = await axios.put(
            `${BASE_URL}/update-election/${id}`,
            new URLSearchParams(data),
            authConfig(token)
        );
        toast.success("Election updated successfully");
        return response.data;
    } catch (err) {
        console.error("Error updating election:", err);
        toast.error(err.response?.data || "Election update failed");
        throw err;
    }
}

export async function deleteElections(ids, token) {
    try {
        const response = await axios.delete(
            `${BASE_URL}/deleteElections`,
            { ...authConfig(token), data: { electionIds: ids } }
        );
        toast.success("Elections deleted successfully");
        return response.data;
    } catch (err) {
        console.error("Error deleting elections:", err);
        toast.error("Failed to delete elections");
        throw err;
    }
}

export async function startElection(electionId, token) {
    try {
        const response = await axios.post(
            `${BASE_URL}/start/${electionId}`,
            null,
            authConfig(token)
        );
        toast.success("Election started successfully");
        return response.data;
    } catch (err) {
        console.error("Error starting election:", err);
        toast.error(err.response?.data || "Failed to start election");
        throw err;
    }
}

export async function endElection(electionId, token) {
    try {
        const response = await axios.post(
            `${BASE_URL}/end/${electionId}`,
            null,
            authConfig(token)
        );
        toast.success("Election ended successfully");
        return response.data;
    } catch (err) {
        console.error("Error ending election:", err);
        toast.error(err.response?.data || "Failed to end election");
        throw err;
    }
}

export async function getActiveElection(token) {
    try {
        const response = await axios.get(
            `${BASE_URL}/active`,
            {
                ...authConfig(token),
                validateStatus: () => true,
            }
        );
        return response.status === 204 ? null : response.data;
    } catch (err) {
        console.error("Error fetching active election:", err);
        toast.error("Failed to fetch active election");
        throw err;
    }
}
